import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from "recharts";
import { Buildings, Sparkle, Gavel, TrendDown, Star, PaperPlaneTilt, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { CLASS_META, fmtDate, fmtNum } from "./meta";

function Tile({ label, value, sub, icon: Icon, testid, color = "#00E5FF" }) {
  return (
    <div className="surface rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted mb-2"><Icon size={14} style={{ color }} />{label}</div>
      <div className="font-display text-3xl font-bold text-fg">{fmtNum(value)}</div>
      {sub && <div className="text-[11px] text-muted mt-1">{sub}</div>}
    </div>
  );
}

function Report({ r }) {
  const rows = [["Prospects détectés", r.detected], ["Prospects qualifiés", r.qualified], ["Prospects contactés", r.contacted],
    ["Prospects convertis", r.converted], ["Taux de conversion observé", r.conversion_rate == null ? "Non calculable (aucun contact)" : `${r.conversion_rate} %`],
    ["Abonnements attribuables", r.attributable_subscriptions || "Aucune attribution disponible"],
    ["MRR attribuable (HT)", r.attributable_mrr_eur_ht == null ? "—" : `${r.attributable_mrr_eur_ht} €`]];
  return (
    <div className="surface rounded-lg p-5" data-testid="lead-monthly-report">
      <div className="overline mb-3">Rapport commercial — {r.month}</div>
      <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-border/50 py-1.5"><dt className="text-muted">{k}</dt><dd className="text-fg font-medium">{v}</dd></div>
        ))}
      </dl>
      <p className="text-[11px] text-muted mt-3">Chiffres calculés uniquement à partir des statuts saisis et des organisations interKO liées. Aucune donnée estimée.</p>
    </div>
  );
}

export default function LeadDashboard() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/leads/dashboard").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  if (!d) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;
  const s = d.stats;
  const classes = Object.entries(CLASS_META).map(([k, m]) => ({ name: m.label, n: d.by_classification[k] || 0, fill: m.color }));
  const srcRow = (k, label) => {
    const x = d.sources?.[k] || {};
    return (
      <div key={k} className="flex items-center justify-between text-xs py-1.5 border-b border-border/50" data-testid={`lead-source-${k}`}>
        <span className="text-fg">{label}</span>
        <span className={x.error ? "text-destructive" : "text-accent"}>{x.error ? `Indisponible (${fmtDate(x.last_error_at)})` : x.last_success ? `OK · ${fmtDate(x.last_success)}` : "Jamais synchronisé"}</span>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="lead-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="ESN détectées (10–199 sal.)" value={s.total} sub={`${fmtNum(s.active)} actives`} icon={Buildings} testid="lead-stat-total" />
        <Tile label="Nouveaux prospects (24 h)" value={s.new_24h} icon={Sparkle} testid="lead-stat-new" />
        <Tile label="RJ / sauvegarde récentes (90 j)" value={s.rj_recent} sub={`${fmtNum(s.procedures_active)} procédures en cours`} icon={Gavel} color="#F97316" testid="lead-stat-rj" />
        <Tile label="Recrutement observé faible" value={s.low_hiring} sub={`${fmtNum(s.hiring_insufficient)} avec données insuffisantes`} icon={TrendDown} color="#F5A623" testid="lead-stat-low-hiring" />
        <Tile label="Prospects prioritaires" value={s.priority} sub="score ≥ 80 ou marqués" icon={Star} color="#22C55E" testid="lead-stat-priority" />
        <Tile label="Déjà contactés" value={s.contacted} icon={PaperPlaneTilt} testid="lead-stat-contacted" />
        <Tile label="Convertis en clients" value={s.converted} icon={CheckCircle} color="#22C55E" testid="lead-stat-converted" />
        <Tile label="Opposition prospection (RNE)" value={s.opposition} sub={`${fmtNum(s.excluded)} exclus manuellement`} icon={Buildings} color="#8A8A93" testid="lead-stat-opposition" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="surface rounded-lg p-5 lg:col-span-2" data-testid="lead-chart-created">
          <div className="overline mb-3">Prospects détectés par jour (90 j)</div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.series_created}>
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8A8A93" }} />
                <YAxis tick={{ fontSize: 10, fill: "#8A8A93" }} width={40} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", fontSize: 12 }} />
                <Area dataKey="count" name="Prospects" stroke="#00E5FF" fill="#00E5FF22" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="surface rounded-lg p-5" data-testid="lead-chart-classes">
          <div className="overline mb-3">Répartition par priorité</div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={classes} layout="vertical">
                <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: "#8A8A93" }} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", fontSize: 12 }} />
                <Bar dataKey="n" name="ESN">{classes.map((c) => <Cell key={c.name} fill={c.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="surface rounded-lg p-5" data-testid="lead-sync-summary">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Synchronisation & sources</div>
            {d.running && <span className="text-xs text-accent flex items-center gap-1"><ArrowClockwise size={12} className="animate-spin" /> en cours</span>}
          </div>
          <p className="text-xs text-muted mb-2">Dernière réussite : <b className="text-fg">{d.last_success ? new Date(d.last_success.finished_at || d.last_success.started_at).toLocaleString("fr-FR") : "—"}</b></p>
          {srcRow("bodacc", "BODACC (DILA)")}
          {srcRow("recherche_entreprises", "Recherche d'entreprises (INSEE/RNE)")}
          <div className="flex items-center justify-between text-xs py-1.5" data-testid="lead-source-hiring">
            <span className="text-fg">Offres observées interKO</span>
            <span className="text-muted">Couverture {d.coverage.coverage_days} j · {d.coverage.quality}</span>
          </div>
        </div>
        <div className="lg:col-span-2"><Report r={d.report} /></div>
      </div>
    </div>
  );
}
